import WaitingPage from '@/components/WaitingPage';
import { useAuth, useOauth } from '@/contexts/AuthContext';
import { authService, type OAuthContext } from '@common/api/authService';
import { useTranslation } from '@client-app/i18n';
import { useEffect, useMemo, useRef, useState } from 'react';

const LOCAL_CALLBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

const isLoopbackOAuthCallbackUrl = (value: string): boolean => {
    try {
        const url = new URL(value);
        return url.protocol === 'http:'
            && LOCAL_CALLBACK_HOSTS.has(url.hostname)
            && url.pathname === '/authorize'
            && Boolean(url.searchParams.get('code'))
            && Boolean(url.searchParams.get('state'));
    } catch {
        return false;
    }
};

const buildOAuthClientReturnKey = (
    oauthContext: OAuthContext,
    workspaceId: string,
): string => [
    workspaceId,
    oauthContext.client_id,
    oauthContext.redirect_uri || '',
    oauthContext.fallback_redirect_uri || '',
    oauthContext.device_id || '',
    oauthContext.login_trace_id,
].join('::');

const OAuthClientReturnPage = () => {
    const { t } = useTranslation();
    const {
        currentWorkspace,
        hideGlobalLoading,
        isLoadingProfile,
        profile,
        user,
    } = useAuth();
    const { getOAuthContextFromSearch, OAUTH_CONTEXT_KEY } = useOauth();
    const oauthContext = useMemo(
        () => getOAuthContextFromSearch(window.location.search),
        [getOAuthContextFromSearch],
    );
    const workspaceId = currentWorkspace?.id || profile?.workspace?.id || null;
    const startedRequestKeyRef = useRef<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        hideGlobalLoading();
        return () => hideGlobalLoading();
    }, [hideGlobalLoading]);

    useEffect(() => {
        if (isLoadingProfile || !user) {
            return undefined;
        }

        if (!oauthContext) {
            setErrorMessage(t('website.waiting.desktopOAuth.missingContext'));
            return undefined;
        }

        if (!workspaceId) {
            setErrorMessage(t('website.waiting.desktopOAuth.missingWorkspace'));
            return undefined;
        }

        const requestKey = buildOAuthClientReturnKey(oauthContext, workspaceId);
        if (startedRequestKeyRef.current === requestKey) {
            return undefined;
        }

        startedRequestKeyRef.current = requestKey;
        let cancelled = false;

        const finishDesktopOAuthReturn = async () => {
            setErrorMessage(null);
            window.localStorage.setItem(OAUTH_CONTEXT_KEY, JSON.stringify(oauthContext));
            const callbackUrl = await authService.getOAuthAuthorizeRedirect(workspaceId, 'fallback');
            if (cancelled) {
                return;
            }
            if (!callbackUrl) {
                throw new Error(t('website.waiting.desktopOAuth.missingCallbackUrl'));
            }
            if (!isLoopbackOAuthCallbackUrl(callbackUrl)) {
                throw new Error(t('website.waiting.desktopOAuth.invalidCallbackUrl'));
            }

            window.location.href = callbackUrl;
        };

        void finishDesktopOAuthReturn().catch((error) => {
            if (cancelled) {
                return;
            }
            console.error('[OAuthClientReturn] Failed to complete desktop OAuth return:', error);
            setErrorMessage(error instanceof Error ? error.message : String(error));
            startedRequestKeyRef.current = null;
        });

        return () => {
            cancelled = true;
        };
    }, [
        OAUTH_CONTEXT_KEY,
        isLoadingProfile,
        oauthContext,
        t,
        user,
        workspaceId,
    ]);

    return (
        <WaitingPage
            title={t('website.waiting.desktopOAuth.title')}
            message={errorMessage || t('website.waiting.desktopOAuth.message')}
            busy={!errorMessage}
            actionLabel={errorMessage ? t('website.waiting.desktopOAuth.retryAction') : undefined}
            onAction={errorMessage ? () => window.location.reload() : undefined}
        />
    );
};

export default OAuthClientReturnPage;
